import moment from 'moment';

import {
  DensityRange1HourByMinutes,
  DensityRange4HoursByHour,
  DensityRange4HoursByMinutes,
  DensityRange12HoursByHour,
  DensityRange15Minutes,
  DensityRange24HoursByHour,
  DensityRange30Minutes,
} from '~/src/dash/globals/constants';
import { EDateGroup } from '~/src/dash/utils/dateGroups';

export type DateRange =
  | 'Custom'
  | 'Today'
  | 'Yesterday'
  | 'Last 7 Days'
  | 'This Week'
  | 'Last Week'
  | 'Last 30 Days'
  | 'This Month'
  | 'Last Month';

export enum DateRangeInt {
  DateRangeUnknown = 0,
  DateRangeToday,
  DateRangeYesterday,
  DateRangeLast7Days,
  DateRangeThisWeek,
  DateRangeLastWeek,
  DateRangeLast30Days,
  DateRangeThisMonth,
  DateRangeLastMonth,
}

export const DateRangeToDateRangeInt: Record<DateRange, DateRangeInt> = {
  Custom: DateRangeInt.DateRangeUnknown,
  Today: DateRangeInt.DateRangeToday,
  Yesterday: DateRangeInt.DateRangeYesterday,
  'Last 7 Days': DateRangeInt.DateRangeLast7Days,
  'This Week': DateRangeInt.DateRangeThisWeek,
  'Last Week': DateRangeInt.DateRangeLastWeek,
  'Last 30 Days': DateRangeInt.DateRangeLast30Days,
  'This Month': DateRangeInt.DateRangeThisMonth,
  'Last Month': DateRangeInt.DateRangeLastMonth,
};

export const DateRangeIntToDateRange: Record<DateRangeInt, DateRange> = {
  [DateRangeInt.DateRangeUnknown]: 'Custom',
  [DateRangeInt.DateRangeToday]: 'Today',
  [DateRangeInt.DateRangeYesterday]: 'Yesterday',
  [DateRangeInt.DateRangeLast7Days]: 'Last 7 Days',
  [DateRangeInt.DateRangeThisWeek]: 'This Week',
  [DateRangeInt.DateRangeLastWeek]: 'Last Week',
  [DateRangeInt.DateRangeLast30Days]: 'Last 30 Days',
  [DateRangeInt.DateRangeThisMonth]: 'This Month',
  [DateRangeInt.DateRangeLastMonth]: 'Last Month',
};

interface IQueryDates {
  startDate: moment.Moment;
  endDate: moment.Moment;
  compareStartDate: moment.Moment;
  compareEndDate: moment.Moment;
}

export const generateQueryDates = (dateRange: DateRange, timezone: string): IQueryDates => {
  const endOfToday = moment().tz(timezone).endOf('day');
  switch (dateRange) {
    case 'Today':
      return {
        startDate: moment().tz(timezone).startOf('day'),
        endDate: endOfToday,
        compareStartDate: moment().tz(timezone).subtract(1, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(1, 'd').endOf('day'),
      };
    case 'Yesterday':
      return {
        startDate: moment().tz(timezone).subtract(1, 'd').startOf('day'),
        endDate: moment().tz(timezone).subtract(1, 'd').endOf('day'),
        compareStartDate: moment().tz(timezone).subtract(2, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(2, 'd').endOf('day'),
      };
    case 'This Week':
      const weekStartDate = moment().tz(timezone).weekday(0).startOf('day');
      return {
        startDate: weekStartDate,
        endDate: endOfToday,
        compareStartDate: moment(weekStartDate).subtract(1, 'week'),
        compareEndDate: moment(endOfToday).subtract(1, 'week'),
      };
    case 'Last Week':
      const lastWeekStartDate = moment().tz(timezone).weekday(0).startOf('day').subtract(1, 'week');
      const lastWeekEndDate = lastWeekStartDate.clone().add(6, 'd').endOf('day');
      return {
        startDate: lastWeekStartDate,
        endDate: lastWeekEndDate,
        compareStartDate: moment(lastWeekStartDate).subtract(1, 'week'),
        compareEndDate: moment(lastWeekEndDate).subtract(1, 'week'),
      };
    case 'Last 30 Days':
      return {
        startDate: moment().tz(timezone).subtract(29, 'd').startOf('day'),
        endDate: endOfToday,
        compareStartDate: moment().tz(timezone).subtract(59, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(30, 'd').endOf('day'),
      };
    case 'This Month':
      const monthStartDate = moment().tz(timezone).startOf('month');
      const monthEndDate = moment().tz(timezone).endOf('day');
      return {
        startDate: monthStartDate,
        endDate: monthEndDate,
        compareStartDate: moment(monthStartDate).subtract(1, 'month'),
        compareEndDate: moment(monthEndDate).subtract(1, 'month'),
      };
    case 'Last Month':
      const lastMonthStartDate = moment().tz(timezone).subtract(1, 'month').startOf('month');
      const lastMonthEndDate = moment()
        .tz(timezone)
        .subtract(1, 'month')
        .endOf('month')
        .endOf('day');
      return {
        startDate: lastMonthStartDate,
        endDate: lastMonthEndDate,
        compareStartDate: moment(lastMonthStartDate).subtract(1, 'month'),
        compareEndDate: moment(lastMonthEndDate).subtract(1, 'month'),
      };
    case 'Last 7 Days':
    default:
      return {
        startDate: moment().tz(timezone).subtract(6, 'd').startOf('day'),
        endDate: endOfToday,
        compareStartDate: moment().tz(timezone).subtract(13, 'd').startOf('day'),
        compareEndDate: moment().tz(timezone).subtract(7, 'd').endOf('day'),
      };
  }
};

export interface IDateTimeRange {
  key: DateRange | TimeRange;
  label: string;
  shortLabel: string;
  extendedLabel?: string;
}

export interface IDateRange extends IDateTimeRange {
  defaultDateGroup: number;
  dateGroupRange: [number, number];
  generateQueryDates: (timezone: string) => IQueryDates;
  isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => boolean;
  getDateRange: (timezone?: string) => number;
}

export const defaultDateRanges: IDateRange[] = [
  {
    key: 'Today',
    label: 'Today',
    shortLabel: 'Today',
    defaultDateGroup: EDateGroup.ByHour,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Today', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Today', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 1,
  },
  {
    key: 'Yesterday',
    label: 'Yesterday',
    shortLabel: 'Yesterday',
    defaultDateGroup: EDateGroup.ByHour,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByDay],
    generateQueryDates: (timezone: string) => generateQueryDates('Yesterday', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Yesterday', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 1,
  },
  {
    key: 'Last 7 Days',
    label: 'Last 7 Days',
    shortLabel: 'L7D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('Last 7 Days', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last 7 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'This Week',
    label: 'Week To Date',
    shortLabel: 'WTD',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('This Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Week', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
  {
    key: 'Last Week',
    label: 'Last Week (Mon-Sun)',
    shortLabel: 'LW',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByHour, EDateGroup.ByWeek],
    generateQueryDates: (timezone: string) => generateQueryDates('Last Week', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Week', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 7,
  },
  {
    key: 'Last 30 Days',
    label: 'Last 30 Days',
    shortLabel: 'L30D',
    defaultDateGroup: EDateGroup.ByDay,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('Last 30 Days', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last 30 Days', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: () => 30,
  },
  {
    key: 'This Month',
    label: 'Month To Date',
    shortLabel: 'MTD',
    defaultDateGroup: EDateGroup.ByMonth,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('This Month', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Month', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('This Month', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
  {
    key: 'Last Month',
    label: 'Last Month',
    shortLabel: 'LM',
    defaultDateGroup: EDateGroup.ByMonth,
    dateGroupRange: [EDateGroup.ByDay, EDateGroup.ByMonth],
    generateQueryDates: (timezone: string) => generateQueryDates('Last Month', timezone),
    isWithinRange: (start: moment.Moment, end: moment.Moment, timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Month', timezone);
      return startDate.isSame(start, 'day') && endDate.isSame(end, 'day');
    },
    getDateRange: (timezone: string) => {
      const { startDate, endDate } = generateQueryDates('Last Month', timezone);
      return endDate.diff(startDate, 'days') + 1;
    },
  },
];

// this will never return undefined because all DateRanges are accounted for
// but because .find returns ' | undefined' we use an "impossible" ternary to remove undefined
export const findDateRange = (key: DateRange): IDateRange => {
  return defaultDateRanges.find((range) => key === range.key) || defaultDateRanges[0];
};

export type TimeRange =
  | '15 Minutes'
  | '30 Minutes'
  | '1 Hour By Minute'
  | '4 Hours By Minute'
  | '4 Hours By Hour'
  | '12 Hours By Hour'
  | '24 Hours By Hour';

export interface ITimeRange extends IDateTimeRange {
  value: number;
}

export const defaultTimeRanges: ITimeRange[] = [
  {
    key: '15 Minutes',
    value: DensityRange15Minutes,
    label: '15 Mins',
    shortLabel: '15 MIN',
    extendedLabel: '15 Minutes',
  },
  {
    key: '30 Minutes',
    value: DensityRange30Minutes,
    label: '30 Mins',
    shortLabel: '30 MIN',
    extendedLabel: '30 Minutes',
  },
  {
    key: '1 Hour By Minute',
    value: DensityRange1HourByMinutes,
    label: '1 Hour',
    shortLabel: '1 HR',
    extendedLabel: '1 Hour By Minute',
  },
  {
    key: '4 Hours By Minute',
    value: DensityRange4HoursByMinutes,
    label: '4 Hours',
    shortLabel: '4 HRS',
    extendedLabel: '4 Hours By Minute',
  },
  {
    key: '4 Hours By Hour',
    value: DensityRange4HoursByHour,
    label: '4 Hours By Hour',
    shortLabel: '4 HRS BY HR',
    extendedLabel: '4 Hours By Hour',
  },
  {
    key: '12 Hours By Hour',
    value: DensityRange12HoursByHour,
    label: '12 Hours By Hour',
    shortLabel: '12 HRS BY HR',
    extendedLabel: '12 Hours By Hour',
  },
  {
    key: '24 Hours By Hour',
    value: DensityRange24HoursByHour,
    label: '24 Hours By Hour',
    shortLabel: '24 HRS BY HR',
    extendedLabel: '24 Hours By Hour',
  },
];

export const findTimeRange = (value: number, ranges = defaultTimeRanges) => {
  return ranges.find((range) => value === range.value) || ranges[0];
};

export const findTimeRangeByKey = (key: string, ranges = defaultTimeRanges) => {
  return ranges.find((range) => key === range.key) || ranges[0];
};
